import React, { useEffect, useMemo, useState } from "react";
import { api, extractError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import SettlementStepper from "./SettlementStepper";
import OutwardWeighmentReceiptActions from "./OutwardWeighmentReceiptActions";
import TransporterFreightFields, { TransporterSameAsCompanyField } from "./TransporterFreightFields";
import DispatchDocsUpload from "./DispatchDocsUpload";
import SettlementReviewTable from "./SettlementReviewTable";
import SalesFreightShortageFields from "./SalesFreightShortageFields";
import FreightTdsFields from "./FreightTdsFields";
import { applyHaltingPatch, computeHaltingSettlement } from "../lib/freightSettlement";
import { formatInr } from "../lib/accountsSettlement";
import { buildSalesSettlementReviewRows, buildSalesTruckReviewRows } from "../lib/salesSettlementReview";
import {
  CommercialWeightBar,
  GstFields,
  SalesVoucherSummaryRibbon,
  SalesWorkspaceHeader,
  TallyPreviewDialog,
  SalesStageBanners,
  SalesAdjustmentBreakdown,
  PostSettlementJournalButton,
  commercialPayload,
  oilPctWarning,
  openSalesTallyPreview,
  seedCommercialForm,
} from "./SalesSettlementChrome";

function SettlementStatusBadge({ status }) {
  const final = (status || "").toUpperCase() === "FINAL";
  return (
    <span
      className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
        final ? "bg-bansal-sage/15 text-primary" : "bg-amber-100 text-amber-900"
      }`}
    >
      {final ? "Final" : "Provisional"}
    </span>
  );
}

const FINAL_STEPS = [
  { id: 1, label: "Party side weight", hint: "GR · quality · lab" },
  { id: 2, label: "Party sales voucher", hint: "Review · edit · adjustments" },
  { id: 3, label: "Freight voucher", hint: "Finalize transporter" },
  { id: 4, label: "Confirm", hint: "Truck summary · issue final" },
];

const DEDUCT_MODES = [
  { value: "NONE", label: "No — do not deduct" },
  { value: "CASH", label: "Yes — driver paid us in cash" },
  { value: "FROM_FREIGHT", label: "Yes — deduct from freight" },
];

function DeductModeField({ label, amountKey, modeKey, freight, setFreight }) {
  return (
    <div className="space-y-1">
      <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</Label>
      <Input
        type="number"
        step="0.01"
        value={freight[amountKey]}
        onChange={(e) => setFreight((f) => ({ ...f, [modeKey]: f[modeKey], [amountKey]: e.target.value }))}
        className="mt-1 h-9"
      />
      <select
        className="mt-1 h-9 w-full rounded-md border border-input bg-background px-2 text-xs"
        value={freight[modeKey] || "NONE"}
        onChange={(e) => setFreight((f) => ({ ...f, [modeKey]: e.target.value }))}
      >
        {DEDUCT_MODES.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

function FreightHaltingFields({ freight, setFreight }) {
  const halt = computeHaltingSettlement({
    halting_applicable: freight.halting_applicable,
    load_date: freight.load_date,
    unload_date: freight.unload_date,
    reached_date: freight.reached_date,
    halting_free_days: freight.halting_free_days,
    halting_rate_per_day: freight.halting_rate_per_day,
    halting_charges: freight.halting_charges,
    halting_days: freight.halting_days,
  });
  const patch = (partial) => setFreight((f) => applyHaltingPatch(f, partial));

  return (
    <div className="col-span-2 sm:col-span-3 rounded-md border border-border/70 bg-muted/20 p-3 space-y-2">
      <label className="flex items-center gap-2 text-xs font-medium">
        <input
          type="checkbox"
          checked={Boolean(freight.halting_applicable)}
          onChange={(e) => patch({ halting_applicable: e.target.checked })}
        />
        Halting applicable
      </label>
      {freight.halting_applicable ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Load date</Label>
            <Input type="date" value={freight.load_date} readOnly className="mt-1 h-9 bg-muted/40" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Reached date</Label>
            <Input type="date" value={freight.reached_date} onChange={(e) => patch({ reached_date: e.target.value })} className="mt-1 h-9" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Unload date</Label>
            <Input type="date" value={freight.unload_date} onChange={(e) => patch({ unload_date: e.target.value })} className="mt-1 h-9" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Free days</Label>
            <Input type="number" min={0} step={1} value={freight.halting_free_days} onChange={(e) => patch({ halting_free_days: e.target.value })} className="mt-1 h-9" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">₹ / day</Label>
            <Input type="number" step="0.01" value={freight.halting_rate_per_day} onChange={(e) => patch({ halting_rate_per_day: e.target.value })} className="mt-1 h-9" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Halting ₹</Label>
            <Input type="number" step="0.01" value={freight.halting_charges} readOnly className="mt-1 h-9 bg-muted/40" />
          </div>
          {freight.reached_date && freight.unload_date ? (
            <p className="col-span-2 sm:col-span-3 text-[11px] text-muted-foreground">
              {halt.halting_elapsed_days} day(s) at destination − {halt.halting_free_days} free ={" "}
              <strong>{halt.halting_days} chargeable</strong>
              {halt.halting_rate_per_day ? <> · ₹{halt.halting_charges}</> : null}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function emptyFreightForm(pack = {}, dispatch = {}) {
  const commissionMode = pack.commission_settlement_mode
    || (pack.deduct_transporter_commission ? "FROM_FREIGHT" : "NONE");
  const dalaMode = pack.dala_settlement_mode
    || (pack.dala_paid_by_driver ? "CASH" : pack.deduct_dala ? "FROM_FREIGHT" : "NONE");
  const biltyMode = pack.bilty_settlement_mode
    || (pack.bilty_paid ? "CASH" : pack.deduct_bilty ? "FROM_FREIGHT" : "NONE");
  const fromPack = pack.load_date ? String(pack.load_date).slice(0, 10) : "";
  const loadDate = fromPack || (dispatch.gross_weighed_at || dispatch.loaded_at || "").toString().slice(0, 10);
  const companyId = pack.transport_company_party_id || pack.transporter_party_id || null;
  const companyName = pack.transport_company_name
    || pack.transport_company_display_name
    || pack.bilty_transporter_name
    || pack.transporter_name
    || pack.transporter_owner_name
    || "";
  const sameAs = pack.transporter_same_as_company != null
    ? Boolean(pack.transporter_same_as_company)
    : !pack.transport_company_party_id || String(pack.transport_company_party_id || "") === String(pack.transporter_party_id || "");
  return {
    transport_company_party_id: companyId,
    transport_company_name: companyName,
    transport_company_display_name: companyName,
    transporter_same_as_company: sameAs,
    transporter_name: pack.transporter_name || pack.transporter_owner_name || companyName || "",
    transporter_owner_name: pack.transporter_owner_name || pack.transporter_name || companyName || "",
    transporter_party_id: sameAs ? companyId : (pack.transporter_party_id || null),
    transporter_display_name: pack.transporter_display_name || pack.transporter_name || (sameAs ? companyName : ""),
    vehicle_number: pack.vehicle_number || dispatch.vehicle_number || "",
    freight_rate_per_qtl: pack.freight_rate_per_qtl != null ? String(pack.freight_rate_per_qtl) : "",
    load_date: loadDate,
    reached_date: pack.reached_date ? String(pack.reached_date).slice(0, 10) : "",
    unload_date: pack.unload_date ? String(pack.unload_date).slice(0, 10) : "",
    halting_applicable: Boolean(pack.halting_applicable),
    halting_free_days: pack.halting_free_days != null ? String(pack.halting_free_days) : "2",
    halting_rate_per_day: pack.halting_rate_per_day != null ? String(pack.halting_rate_per_day) : "",
    halting_days: pack.halting_days != null ? String(pack.halting_days) : "0",
    halting_charges: pack.halting_charges != null ? String(pack.halting_charges) : "",
    advance_at_dispatch: pack.advance_at_dispatch != null ? String(pack.advance_at_dispatch) : (pack.advance != null ? String(pack.advance) : ""),
    transporter_commission: pack.transporter_commission != null ? String(pack.transporter_commission) : "",
    commission_settlement_mode: commissionMode,
    dala: pack.dala != null ? String(pack.dala) : "",
    dala_settlement_mode: dalaMode,
    bilty_charges: pack.bilty_charges != null ? String(pack.bilty_charges) : "",
    bilty_settlement_mode: biltyMode,
    bilty_number: pack.bilty_number || "",
    shortage_qtl: pack.shortage_qtl != null ? String(pack.shortage_qtl) : "",
    shortage_amount: pack.shortage_amount != null ? String(pack.shortage_amount) : "",
    shortage_allowance_mode: pack.shortage_allowance_mode || "",
    shortage_allowance_value: pack.shortage_allowance_value != null ? String(pack.shortage_allowance_value) : "",
    other_deductions: pack.other_deductions != null ? String(pack.other_deductions) : "",
    from_place: pack.from_place || "",
    to_place: pack.to_place || "",
    pan_no: pack.pan_no || pack.truck_owner_pan || "",
    truck_owner_pan: pack.truck_owner_pan || pack.pan_no || "",
    tds_payee_category: pack.tds_payee_category || "",
    tds_small_transporter_exempt: Boolean(pack.tds_small_transporter_exempt),
    tds_manual_override: Boolean(pack.tds_manual_override),
    tds_pct: pack.tds_pct != null && pack.tds_manual_override ? String(pack.tds_pct) : "",
  };
}

function freightPayloadFromForm(form) {
  const num = (v) => (v === "" || v == null ? undefined : Number(v));
  const halt = computeHaltingSettlement({
    halting_applicable: form.halting_applicable,
    load_date: form.load_date,
    unload_date: form.unload_date,
    reached_date: form.reached_date,
    halting_free_days: form.halting_free_days,
    halting_rate_per_day: form.halting_rate_per_day,
    halting_charges: form.halting_charges,
    halting_days: form.halting_days,
  });
  const commissionMode = form.commission_settlement_mode || "NONE";
  const dalaMode = form.dala_settlement_mode || "NONE";
  const biltyMode = form.bilty_settlement_mode || "NONE";
  const sameAs = Boolean(form.transporter_same_as_company);
  const companyName = (form.transport_company_name || form.transport_company_display_name || "").trim();
  return {
    transport_company_party_id: form.transport_company_party_id || undefined,
    transport_company_name: companyName || undefined,
    transport_company_display_name: companyName || undefined,
    transporter_same_as_company: sameAs,
    bilty_transporter_name: companyName || undefined,
    transporter_name: sameAs ? (companyName || undefined) : (form.transporter_name?.trim() || undefined),
    transporter_owner_name: sameAs
      ? (companyName || undefined)
      : ((form.transporter_owner_name || form.transporter_name)?.trim() || undefined),
    transporter_party_id: sameAs
      ? (form.transport_company_party_id || undefined)
      : (form.transporter_party_id || undefined),
    transporter_display_name: sameAs
      ? (companyName || undefined)
      : (form.transporter_display_name?.trim() || form.transporter_name?.trim() || undefined),
    vehicle_number: form.vehicle_number?.trim() || undefined,
    freight_rate_per_qtl: num(form.freight_rate_per_qtl),
    load_date: form.load_date || undefined,
    reached_date: form.reached_date || undefined,
    unload_date: form.unload_date || undefined,
    halting_applicable: Boolean(form.halting_applicable),
    halting_free_days: form.halting_applicable ? num(form.halting_free_days) : 0,
    halting_rate_per_day: form.halting_applicable ? num(form.halting_rate_per_day) : 0,
    halting_days: halt.halting_days,
    halting_charges: form.halting_applicable ? halt.halting_charges : 0,
    advance_at_dispatch: num(form.advance_at_dispatch),
    transporter_commission: num(form.transporter_commission),
    commission_settlement_mode: commissionMode,
    deduct_transporter_commission: commissionMode === "FROM_FREIGHT",
    dala: num(form.dala),
    dala_settlement_mode: dalaMode,
    deduct_dala: dalaMode === "FROM_FREIGHT",
    dala_paid_by_driver: dalaMode === "CASH",
    bilty_charges: num(form.bilty_charges),
    bilty_settlement_mode: biltyMode,
    deduct_bilty: biltyMode === "FROM_FREIGHT",
    bilty_paid: biltyMode === "CASH",
    bilty_number: form.bilty_number?.trim() || undefined,
    shortage_qtl: num(form.shortage_qtl),
    shortage_amount: num(form.shortage_amount),
    shortage_allowance_mode: form.shortage_allowance_mode || undefined,
    shortage_allowance_value: num(form.shortage_allowance_value),
    other_deductions: num(form.other_deductions),
    from_place: form.from_place?.trim() || undefined,
    to_place: form.to_place?.trim() || undefined,
    pan_no: form.pan_no?.trim() || form.truck_owner_pan?.trim() || undefined,
    truck_owner_pan: form.truck_owner_pan?.trim() || form.pan_no?.trim() || undefined,
    tds_payee_category: form.tds_payee_category || undefined,
    tds_small_transporter_exempt: Boolean(form.tds_small_transporter_exempt),
    tds_manual_override: Boolean(form.tds_manual_override),
    tds_pct: form.tds_manual_override ? num(form.tds_pct) : undefined,
  };
}

function AdjustmentLinesEditor({ title, hint, lines, onChange, addLabel }) {
  const update = (idx, patch) => {
    onChange(lines.map((row, i) => (i === idx ? { ...row, ...patch } : row)));
  };
  const remove = (idx) => onChange(lines.filter((_, i) => i !== idx));
  return (
    <div className="rounded-md border border-border bg-muted/20 p-4 space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-xs font-medium text-foreground">{title}</p>
          {hint ? <p className="text-[10px] text-muted-foreground mt-0.5">{hint}</p> : null}
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-7 text-xs"
          onClick={() => onChange([...lines, { particular: "", amount: "" }])}
        >
          {addLabel}
        </Button>
      </div>
      {lines.length === 0 ? (
        <p className="text-[11px] text-muted-foreground">None — use + to add a line with particular and amount.</p>
      ) : (
        <div className="space-y-2">
          {lines.map((row, idx) => (
            <div key={idx} className="grid grid-cols-1 sm:grid-cols-[1fr_120px_auto] gap-2 items-end">
              <div>
                <Label className="text-[10px] uppercase text-muted-foreground">Particular</Label>
                <Input
                  placeholder="e.g. Bank charges"
                  value={row.particular}
                  onChange={(e) => update(idx, { particular: e.target.value })}
                  className="mt-1 h-8 text-xs"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase text-muted-foreground">Amount ₹</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0"
                  value={row.amount}
                  onChange={(e) => update(idx, { amount: e.target.value })}
                  className="mt-1 h-8 text-xs"
                />
              </div>
              <Button type="button" size="sm" variant="ghost" className="h-8 text-xs" onClick={() => remove(idx)}>
                Remove
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * 3-step finalization wizard for provisional sales vouchers (Provisional tab).
 * Saves draft on every Next.
 */
export default function FinalSettlementWizard({ dispatch, onSaved }) {
  const allocs = dispatch.contract_allocations?.length
    ? dispatch.contract_allocations
    : [{
        sales_contract_id: dispatch.sales_contract_id,
        contract_number: dispatch.sales_contract_number,
        rate: dispatch.contract_rate,
        pricing_mode: dispatch.pricing_mode,
        delivery_condition: dispatch.delivery_condition,
        product_family: dispatch.product_family,
      }];

  const isFor =
    (dispatch.delivery_condition || "").toUpperCase() === "FOR"
    || allocs.some((a) => (a.delivery_condition || "").toUpperCase() === "FOR");
  const isBran =
    (dispatch.product_family || "").toUpperCase() === "RICE_BRAN"
    || allocs.some((a) => (a.product_family || "").toUpperCase() === "RICE_BRAN");
  const needsOil =
    isBran
    || (dispatch.pricing_mode || "").toUpperCase() === "PER_OIL_POINT"
    || allocs.some((a) => (a.pricing_mode || "").toUpperCase() === "PER_OIL_POINT");

  const [step, setStep] = useState(() => Math.min(4, Math.max(1, Number(dispatch.finalize_wizard_step) || 1)));
  const [completed, setCompleted] = useState(() => {
    const s = Number(dispatch.finalize_wizard_step) || 1;
    return new Set(s > 1 ? Array.from({ length: s - 1 }, (_, i) => i + 1) : []);
  });
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState(null);
  const [preview, setPreview] = useState(null);
  const [settlementDocs, setSettlementDocs] = useState(() => dispatch.settlement_docs || []);

  const [rate, setRate] = useState(
    dispatch.contract_rate != null ? String(dispatch.contract_rate) : "",
  );
  const [weightCut, setWeightCut] = useState(
    dispatch.weight_cut_pct != null ? String(dispatch.weight_cut_pct) : "0",
  );
  const [priceCut, setPriceCut] = useState(
    dispatch.price_cut_pct != null ? String(dispatch.price_cut_pct) : "0",
  );
  const [priceCutBasis, setPriceCutBasis] = useState(
    (dispatch.price_cut_basis || "GROSS_PCT").toUpperCase() === "PER_QTL" ? "PER_QTL" : "GROSS_PCT",
  );
  const [note, setNote] = useState(dispatch.accounts_note || "");
  const [lines, setLines] = useState(() =>
    allocs.map((a) => ({
      sales_contract_id: a.sales_contract_id,
      allocation_id: a.id,
      contract_number: a.contract_number || "",
      oil_pct: a.oil_pct != null ? String(a.oil_pct) : "",
      ffa_pct: a.ffa_pct != null ? String(a.ffa_pct) : "",
      party_weight_qtl: a.party_weight_qtl != null ? String(a.party_weight_qtl) : "",
      bags: a.bags != null ? String(a.bags) : (dispatch.bags_loaded != null ? String(dispatch.bags_loaded) : ""),
    })),
  );
  const [otherDeductions, setOtherDeductions] = useState(() =>
    (dispatch.other_deductions || []).map((x) => ({
      particular: x.particular || "",
      amount: x.amount != null ? String(x.amount) : "",
    })),
  );
  const [otherAdditions, setOtherAdditions] = useState(() =>
    (dispatch.other_additions || []).map((x) => ({
      particular: x.particular || "",
      amount: x.amount != null ? String(x.amount) : "",
    })),
  );
  const [includeFreight, setIncludeFreight] = useState(Boolean(dispatch.include_freight ?? isFor));
  const [freight, setFreight] = useState(() => emptyFreightForm(dispatch.freight_pack || {}, dispatch));
  const [commercial, setCommercial] = useState(() => seedCommercialForm(dispatch));
  const [tallyPreviewIds, setTallyPreviewIds] = useState(null);

  const patchCommercial = (patch) => setCommercial((prev) => ({ ...prev, ...patch }));

  const updateLine = (idx, patch) => {
    setLines((prev) => prev.map((row, i) => (i === idx ? { ...row, ...patch } : row)));
  };

  const buildAllocations = () =>
    lines.map((row) => {
      const payload = { sales_contract_id: Number(row.sales_contract_id) };
      if (row.allocation_id) payload.allocation_id = Number(row.allocation_id);
      if (row.oil_pct !== "") payload.oil_pct = Number(row.oil_pct);
      if (row.ffa_pct !== "") payload.ffa_pct = Number(row.ffa_pct);
      if (row.party_weight_qtl !== "") payload.party_weight_qtl = Number(row.party_weight_qtl);
      if (row.bags !== "") payload.bags = Number(row.bags);
      return payload;
    });

  const adjustmentPayload = (rows) =>
    rows
      .filter((r) => r.particular.trim() && Number(r.amount) > 0)
      .map((r) => ({ particular: r.particular.trim(), amount: Number(r.amount) }));

  const draftBody = (wizardStep) => ({
    // Oil-point uses contract ₹/oil-pt — do not send voucher ₹/qtl as rate override.
    rate_per_qtl: needsOil ? undefined : (rate ? Number(rate) : undefined),
    weight_cut_pct: Number(weightCut) || 0,
    price_cut_pct: Number(priceCut) || 0,
    price_cut_basis: priceCutBasis,
    accounts_note: note.trim() || undefined,
    allocations: buildAllocations(),
    freight_slip: freightPayloadFromForm(freight),
    include_freight: isFor ? true : includeFreight,
    other_deductions: adjustmentPayload(otherDeductions),
    other_additions: adjustmentPayload(otherAdditions),
    settlement_options: commercialPayload(commercial),
    finalize_wizard_step: wizardStep,
  });

  const saveDraft = async (wizardStep) => {
    const { data } = await api.patch(`/outward-dispatches/${dispatch.id}/settlement-draft`, draftBody(wizardStep));
    if (data?.dispatch?.settlement_docs) setSettlementDocs(data.dispatch.settlement_docs);
    onSaved?.(data.dispatch || data);
    return data;
  };

  const loadPreview = async () => {
    const { data } = await api.post(`/outward-dispatches/${dispatch.id}/settlement-preview`, {
      rate_per_qtl: needsOil ? undefined : (rate ? Number(rate) : undefined),
      weight_cut_pct: Number(weightCut) || 0,
      price_cut_pct: Number(priceCut) || 0,
      price_cut_basis: priceCutBasis,
      allocations: buildAllocations(),
      freight_slip: freightPayloadFromForm(freight),
      include_freight: isFor ? true : includeFreight,
      other_deductions: adjustmentPayload(otherDeductions),
      other_additions: adjustmentPayload(otherAdditions),
      settlement_options: commercialPayload(commercial),
    });
    setPreview(data);
    return data;
  };

  const validateStep1 = () => {
    for (const row of lines) {
      if (isFor && (row.party_weight_qtl === "" || Number(row.party_weight_qtl) <= 0)) {
        toast.error(`Enter party side weight for ${row.contract_number || row.sales_contract_id}`);
        return false;
      }
      if (needsOil && (row.oil_pct === "" || Number(row.oil_pct) <= 0)) {
        toast.error(`Enter lab oil % for ${row.contract_number || row.sales_contract_id}`);
        return false;
      }
      if (needsOil && row.oil_pct !== "") {
        const warn = oilPctWarning(row.oil_pct);
        if (warn) toast.warning(`${row.contract_number || row.sales_contract_id}: ${warn}`);
      }
    }
    if (isFor) {
      const hasGr = settlementDocs.some((d) => d.kind === "GOODS_RECEIPT");
      if (!hasGr) {
        toast.error("Upload at least one goods receipt for FOR settlement");
        return false;
      }
    }
    if (isBran) {
      const hasLab = settlementDocs.some((d) => d.kind === "LAB_REPORT");
      if (!hasLab) {
        toast.error("Upload lab report for rice bran settlement");
        return false;
      }
    }
    return true;
  };

  const goNext = async () => {
    try {
      setBusy(true);
      if (step === 1) {
        if (!validateStep1()) return;
        await saveDraft(2);
        await loadPreview();
        setCompleted((prev) => new Set([...prev, 1]));
        setStep(2);
        toast.success("Step 1 saved");
        return;
      }
      if (step === 2) {
        await saveDraft(3);
        await loadPreview();
        setCompleted((prev) => new Set([...prev, 2]));
        setStep(3);
        toast.success("Party voucher draft saved");
        return;
      }
      if (step === 3) {
        if ((isFor || includeFreight) && freight.halting_applicable && !freight.reached_date) {
          toast.error("Enter reached date for halting");
          return;
        }
        await saveDraft(4);
        await loadPreview();
        setCompleted((prev) => new Set([...prev, 3]));
        setStep(4);
        toast.success("Freight draft saved");
        return;
      }
      if (step === 4) {
        await saveDraft(4);
        const { data } = await api.post(`/outward-dispatches/${dispatch.id}/accounts-finalize`, draftBody(4));
        setIssued(data);
        toast.success(
          dispatch.accounts_refinalize_required
            ? `Voucher updated ${data.voucher?.code}`
            : `Final settlement ${data.voucher?.code}`,
        );
        onSaved?.(data.dispatch || data);
      }
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (step === 2 || step === 3 || step === 4) {
      const t = setTimeout(() => {
        loadPreview().catch(() => {});
      }, 400);
      return () => clearTimeout(t);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, commercial, weightCut, priceCut, priceCutBasis, rate, lines, otherDeductions, otherAdditions]);

  useEffect(() => {
    if (step !== 3) return undefined;
    const t = setTimeout(() => {
      loadPreview().catch(() => {});
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, freight.shortage_allowance_mode, freight.shortage_allowance_value, freight.freight_rate_per_qtl]);

  const step1DocKinds = useMemo(() => {
    const kinds = [];
    if (isFor) kinds.push({ value: "GOODS_RECEIPT", label: "Goods receipt" });
    if (isBran || needsOil) kinds.push({ value: "LAB_REPORT", label: "Lab report" });
    return kinds;
  }, [isFor, isBran, needsOil]);

  if (issued) {
    const settled = {
      ...dispatch,
      ...(issued.dispatch || {}),
      sales_settlement_status: "FINAL",
      adjustment_journal_id: issued.adjustment_journal?.id || dispatch.adjustment_journal_id,
      adjustment_journal_code: issued.adjustment_journal?.code || dispatch.adjustment_journal_code,
      adjustment_breakdown: issued.adjustment_breakdown || dispatch.adjustment_breakdown,
    };
    return (
      <div className="space-y-3 pt-2 border-t border-border/60">
        <p className="text-sm link-brand font-medium flex items-center gap-2">
          Final sales voucher <span className="font-mono">{issued.voucher?.code}</span>
          <SettlementStatusBadge status="FINAL" />
        </p>
        <SalesStageBanners dispatch={settled} />
        <SalesAdjustmentBreakdown breakdown={settled.adjustment_breakdown} />
        <OutwardWeighmentReceiptActions dispatchId={dispatch.id} label={dispatch.code} compact={false} />
        <div className="flex flex-wrap gap-2">
          <PostSettlementJournalButton dispatch={settled} onSynced={() => onSaved?.()} />
          <Button type="button" size="sm" variant="outline" onClick={() => onSaved?.()}>Done</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 min-w-0">
      <SalesWorkspaceHeader
        dispatch={dispatch}
        voucherMode="FINAL"
        canEdit
        saving={busy}
        onSaveDraft={() => saveDraft(step).then(() => toast.success("Draft saved")).catch((e) => toast.error(extractError(e)))}
        onPreviewTally={() => openSalesTallyPreview(dispatch, setTallyPreviewIds)}
        onFinalize={() => {
          if (step !== 4) {
            setStep(4);
            return;
          }
          goNext();
        }}
        finalizeLabel={dispatch.accounts_refinalize_required ? "Save updated settlement" : "Finalize & Sync"}
      />
      <SalesStageBanners dispatch={dispatch} />
      <SalesAdjustmentBreakdown breakdown={preview?.adjustment_breakdown || dispatch.adjustment_breakdown} />
      <CommercialWeightBar
        millQtl={dispatch.net_weight_qtl}
        partyQtl={lines[0]?.party_weight_qtl}
        billableQtl={preview?.totals?.billable_qtl}
        bags={lines[0]?.bags || dispatch.bags_loaded}
        deliveryCondition={isFor ? "FOR" : dispatch.delivery_condition}
        partyEditable={isFor}
        partyValue={lines[0]?.party_weight_qtl ?? ""}
        onPartyChange={(v) => updateLine(0, { party_weight_qtl: v })}
      />
      <SalesVoucherSummaryRibbon totals={preview?.totals || { receivable: dispatch.sales_voucher_net }} />
      <TallyPreviewDialog
        open={Boolean(tallyPreviewIds?.length)}
        onOpenChange={(open) => { if (!open) setTallyPreviewIds(null); }}
        voucherIds={tallyPreviewIds || []}
      />

      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <SettlementStatusBadge status="PROVISIONAL" />
        <span className="font-mono text-foreground">{dispatch.sales_voucher_code}</span>
        {isFor ? <span className="text-amber-800">FOR</span> : null}
        {isBran ? <span className="text-amber-800">Rice bran</span> : null}
      </div>

      <SettlementStepper
        steps={FINAL_STEPS}
        currentStep={step}
        completed={completed}
        onSelect={(id) => {
          if (completed.has(id) || id === step || completed.has(id - 1) || id === 1) setStep(id);
        }}
        testId="final-settlement-steps"
      />

      {step === 1 && (
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">
            {isFor
              ? "Enter party side weight, quality cut if any, and attach goods receipt(s)."
              : "Confirm bags / quality cut. Truck-load uses weighbridge net."}
            {isBran ? " Rice bran requires lab oil/FFA and lab report." : ""}
          </p>
          {lines.map((row, idx) => (
            <div key={row.allocation_id || idx} className="rounded-lg border border-border/70 p-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div className="sm:col-span-4 text-sm font-medium">{row.contract_number || allocs[idx]?.contract_number}</div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Bags</Label>
                <Input type="number" value={row.bags} onChange={(e) => updateLine(idx, { bags: e.target.value })} className="mt-1 h-9" />
              </div>
              {isFor ? (
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Party side weight (qtl)</Label>
                  <Input type="number" step="0.01" value={row.party_weight_qtl} onChange={(e) => updateLine(idx, { party_weight_qtl: e.target.value })} className="mt-1 h-9" />
                  {isFor ? (
                    <p className="text-[10px] text-muted-foreground mt-1">
                      Billable = min(our net, party wt)
                    </p>
                  ) : null}
                </div>
              ) : null}
              {needsOil ? (
                <>
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Lab oil %</Label>
                    <Input type="number" step="0.01" value={row.oil_pct} onChange={(e) => updateLine(idx, { oil_pct: e.target.value })} className="mt-1 h-9" />
                    {oilPctWarning(row.oil_pct) ? (
                      <p className="text-[10px] text-amber-800 dark:text-amber-200 mt-1">{oilPctWarning(row.oil_pct)}</p>
                    ) : null}
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Lab FFA %</Label>
                    <Input type="number" step="0.01" value={row.ffa_pct} onChange={(e) => updateLine(idx, { ffa_pct: e.target.value })} className="mt-1 h-9" />
                  </div>
                </>
              ) : null}
            </div>
          ))}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 max-w-2xl">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Quality weight cut %</Label>
              <Input type="number" step="0.01" min="0" value={weightCut} onChange={(e) => setWeightCut(e.target.value)} className="mt-1 h-9" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Quality price cut basis</Label>
              <select
                className="mt-1 h-9 w-full rounded-md border border-input bg-background px-2 text-xs"
                value={priceCutBasis}
                onChange={(e) => setPriceCutBasis(e.target.value)}
              >
                <option value="GROSS_PCT">% Gross</option>
                <option value="PER_QTL">Per qtl (₹)</option>
              </select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
                {priceCutBasis === "PER_QTL" ? "Quality price cut ₹/qtl" : "Quality price cut %"}
              </Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={priceCut}
                onChange={(e) => setPriceCut(e.target.value)}
                className="mt-1 h-9"
                placeholder={priceCutBasis === "PER_QTL" ? "e.g. 50" : "e.g. 1.5"}
              />
            </div>
          </div>
          {step1DocKinds.length ? (
            <div className="rounded-md border border-border/70 p-3 space-y-2">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Attachments</p>
              <DispatchDocsUpload
                dispatchId={dispatch.id}
                docs={settlementDocs.filter((d) => step1DocKinds.some((k) => k.value === d.kind))}
                kinds={step1DocKinds}
                hint={isFor ? "Multiple goods receipts allowed." : "Lab report required for bran."}
                onUploaded={(next) => {
                  if (next?.settlement_docs) setSettlementDocs(next.settlement_docs);
                  onSaved?.(next);
                }}
              />
            </div>
          ) : null}
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Sales party settlement review
              </p>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                Same layout as purchase: each line shows contract condition, value as per contract, and final figure. Adjust rate / quality cuts, then add other deductions or additions.
              </p>
            </div>
            {busy ? (
              <span className="text-xs text-muted-foreground">Calculating…</span>
            ) : null}
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Accounts note</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} className="mt-1 h-9" placeholder="Optional note for accounts" />
          </div>

          {(preview?.settlements || []).length ? (
            <div className="space-y-4">
              <SettlementReviewTable
                title="Truck commercial terms"
                subtitle="CD, brokerage, bardana and GST — pre-filled from the sales contract, editable before posting"
                rows={buildSalesTruckReviewRows(preview.totals || {}, commercial)}
                form={commercial}
                onUpdate={(field, value) => patchCommercial({ [field]: value })}
              />
              <GstFields form={commercial} onPatch={patchCommercial} />
              {preview.settlements.map((ln) => (
                <SettlementReviewTable
                  key={`${ln.sales_contract_id}-${ln.allocation_id || 0}`}
                  title={`${ln.contract_number || ln.sales_contract_id}${ln.pricing_mode === "PER_OIL_POINT" ? " · oil-point" : ""}`}
                  subtitle={`${ln.bags ?? "—"} bags · ${ln.net_qtl ?? "—"} qtl WB net · ${ln.billable_qtl ?? "—"} qtl billable`}
                  rows={buildSalesSettlementReviewRows(ln, {
                    revised_rate: rate,
                    weight_cut_pct: weightCut,
                    price_cut_pct: priceCut,
                    price_cut_basis: priceCutBasis,
                    accounts_note: note,
                    ...commercial,
                  }, { isBran })}
                  form={{
                    revised_rate: rate,
                    weight_cut_pct: weightCut,
                    price_cut_pct: priceCut,
                    price_cut_basis: priceCutBasis,
                    ...commercial,
                  }}
                  onUpdate={(field, value) => {
                    if (field === "revised_rate") setRate(value);
                    else if (field === "weight_cut_pct") setWeightCut(value);
                    else if (field === "price_cut_pct") setPriceCut(value);
                    else if (field === "price_cut_basis") setPriceCutBasis(value);
                    else patchCommercial({ [field]: value });
                  }}
                />
              ))}

            </div>
          ) : (
            <p className="text-xs text-muted-foreground rounded-md border border-dashed border-border p-4">
              Click Refresh calculations to load the settlement review (same as purchase party settlement).
            </p>
          )}

          <AdjustmentLinesEditor
            title="Other deductions (from party)"
            hint="Reduces buyer receivable — e.g. bank charges, shortage claim."
            addLabel="+ Deduction"
            lines={otherDeductions}
            onChange={setOtherDeductions}
          />
          <AdjustmentLinesEditor
            title="Other additions (charged on party)"
            hint="Increases buyer receivable — e.g. halting charged on party."
            addLabel="+ Addition"
            lines={otherAdditions}
            onChange={setOtherAdditions}
          />

          {preview?.totals ? (
            <p className="text-sm">
              Buyer receivable{" "}
              <span className="font-semibold tabular">{formatInr(preview.totals.receivable)}</span>
              <span className="text-xs text-muted-foreground ml-2">
                (before adj. {formatInr(preview.totals.receivable_before_adjustments ?? preview.totals.receivable)})
              </span>
            </p>
          ) : null}

          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => loadPreview().catch((e) => toast.error(extractError(e)))}
          >
            Refresh calculations
          </Button>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-3">
          {!isFor ? (
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={includeFreight} onChange={(e) => setIncludeFreight(e.target.checked)} />
              Include / finalize freight voucher
            </label>
          ) : (
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground">FOR — freight always finalized</p>
          )}
          {(isFor || includeFreight) ? (
            <div className="space-y-3">
              <div className="space-y-3 rounded-md border border-border/70 p-3">
                <TransporterFreightFields
                  role="company"
                  slip={freight}
                  onSlipChange={(next) => setFreight((f) => ({ ...f, ...next }))}
                  vehicleNumber={freight.vehicle_number || dispatch.vehicle_number || ""}
                  ownerName={freight.transport_company_name || freight.transporter_name}
                />
                <TransporterSameAsCompanyField
                  slip={freight}
                  onSlipChange={(next) => setFreight((f) => ({ ...f, ...next }))}
                />
                {!freight.transporter_same_as_company ? (
                  <TransporterFreightFields
                    role="transporter"
                    slip={freight}
                    onSlipChange={(next) => setFreight((f) => ({ ...f, ...next }))}
                    vehicleNumber={freight.vehicle_number || dispatch.vehicle_number || ""}
                    ownerName={freight.transporter_owner_name || freight.transporter_name}
                  />
                ) : (
                  <p className="text-[11px] text-muted-foreground">
                    Commission (if deducted) posts to the transport company ledger.
                  </p>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Bilty / LR-RR no.</Label>
                  <Input value={freight.bilty_number} onChange={(e) => setFreight((f) => ({ ...f, bilty_number: e.target.value }))} className="mt-1 h-9" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Carrier name / agent</Label>
                  <Input
                    value={freight.carrier_name || freight.bilty_transporter_name || freight.transport_company_name || ""}
                    onChange={(e) => setFreight((f) => ({
                      ...f,
                      carrier_name: e.target.value,
                      bilty_transporter_name: e.target.value,
                    }))}
                    className="mt-1 h-9"
                  />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Freight ₹/qtl</Label>
                  <Input type="number" step="0.01" value={freight.freight_rate_per_qtl} onChange={(e) => setFreight((f) => ({ ...f, freight_rate_per_qtl: e.target.value }))} className="mt-1 h-9" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Advance</Label>
                  <Input type="number" step="0.01" value={freight.advance_at_dispatch} onChange={(e) => setFreight((f) => ({ ...f, advance_at_dispatch: e.target.value }))} className="mt-1 h-9" />
                </div>
                <FreightHaltingFields freight={freight} setFreight={setFreight} />
                <DeductModeField label="Transporter commission ₹" amountKey="transporter_commission" modeKey="commission_settlement_mode" freight={freight} setFreight={setFreight} />
                <DeductModeField label="Dala ₹" amountKey="dala" modeKey="dala_settlement_mode" freight={freight} setFreight={setFreight} />
                <DeductModeField label="Bilty charges ₹" amountKey="bilty_charges" modeKey="bilty_settlement_mode" freight={freight} setFreight={setFreight} />
                <SalesFreightShortageFields
                  freight={freight}
                  setFreight={setFreight}
                  settlement={preview?.freight_settlement}
                />
                <FreightTdsFields
                  className="col-span-2 sm:col-span-3"
                  value={freight}
                  settlement={preview?.freight_settlement}
                  onChange={(next) => setFreight((f) => ({ ...f, ...next }))}
                />
              </div>
              {preview?.freight_settlement ? (
                <div className="rounded-md bg-muted/40 p-3 text-xs space-y-1">
                  <p>Gross freight: <strong>₹{(preview.freight_settlement.gross_freight || 0).toLocaleString("en-IN")}</strong></p>
                  {(preview.freight_settlement.shortage_deduction || 0) > 0 ? (
                    <p>
                      Shortage deduction:{" "}
                      <strong>₹{(preview.freight_settlement.shortage_deduction || 0).toLocaleString("en-IN")}</strong>
                      {" · "}net {preview.freight_settlement.net_shortage_qtl} qtl
                    </p>
                  ) : null}
                  {(preview.freight_settlement.tds_amount || 0) > 0 || preview.freight_settlement.tds_reason ? (
                    <p>
                      TDS: <strong>₹{(preview.freight_settlement.tds_amount || 0).toLocaleString("en-IN")}</strong>
                      {preview.freight_settlement.tds_reason ? (
                        <span className="text-muted-foreground"> — {preview.freight_settlement.tds_reason}</span>
                      ) : null}
                    </p>
                  ) : null}
                  <p>Net freight payable: <strong>₹{(preview.freight_settlement.net_freight_payable || 0).toLocaleString("en-IN")}</strong></p>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs mt-1"
                    disabled={busy}
                    onClick={() => loadPreview().catch((e) => toast.error(extractError(e)))}
                  >
                    Refresh freight calc
                  </Button>
                </div>
              ) : null}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Freight voucher will be waived for this truck-load.</p>
          )}
        </div>
      )}

      {step === 4 && (
        <div className="space-y-3">
          <div>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Confirm final settlement
            </p>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Short truck-level party and freight summary — review, then issue final vouchers.
            </p>
          </div>

          <div className="rounded-md border border-border/70 p-3 space-y-2">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium">
              Truck-level · party settlement
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Vehicle</p>
                <p className="font-medium">{dispatch.vehicle_number || "—"}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Billable</p>
                <p className="font-medium">{preview?.totals?.billable_qtl ?? "—"} qtl</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Full amount</p>
                <p className="font-medium">{formatInr(preview?.totals?.full_amount)}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Buyer receivable</p>
                <p className="font-medium text-primary">{formatInr(preview?.totals?.receivable)}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
              <span>CD {formatInr(preview?.totals?.cd_amount)}</span>
              <span>Brokerage {formatInr(preview?.totals?.brokerage_amount)}</span>
              {(preview?.totals?.other_deductions_total || 0) > 0 ? (
                <span>Other ded. −{formatInr(preview.totals.other_deductions_total)}</span>
              ) : null}
              {(preview?.totals?.other_additions_total || 0) > 0 ? (
                <span>Other add. +{formatInr(preview.totals.other_additions_total)}</span>
              ) : null}
              {note.trim() ? <span>Note: {note.trim()}</span> : null}
            </div>
            {(preview?.settlements || []).some((ln) => ln.pricing_mode === "PER_OIL_POINT") ? (
              <div className="pt-1 border-t border-border/50 text-[11px] text-muted-foreground space-y-0.5">
                {(preview.settlements || [])
                  .filter((ln) => ln.pricing_mode === "PER_OIL_POINT")
                  .map((ln) => (
                    <p key={ln.allocation_id || ln.sales_contract_id}>
                      {ln.contract_number || "Oil-point"}: ₹{ln.full_rate}/oil-pt
                      {" × "}{ln.effective_oil} oil
                      {" × "}{ln.billable_qtl} qtl
                      {" = "}{formatInr(ln.full_amount)}
                      {ln.lab_oil != null ? ` (lab oil ${ln.lab_oil}%)` : " (provisional at oil base)"}
                    </p>
                  ))}
              </div>
            ) : null}
            {(preview?.settlements || []).some((ln) => ln.billable_weight_basis === "MIN_MILL_PARTY") ? (
              <div className="pt-1 border-t border-border/50 text-[11px] text-muted-foreground space-y-0.5">
                {(preview.settlements || [])
                  .filter((ln) => ln.billable_weight_basis === "MIN_MILL_PARTY")
                  .map((ln) => (
                    <p key={`for-${ln.allocation_id || ln.sales_contract_id}`}>
                      FOR billable = min(our {ln.mill_weight_qtl} qtl, party {ln.party_weight_qtl} qtl)
                      {" → "}{ln.net_qtl ?? ln.billable_qtl} qtl
                    </p>
                  ))}
              </div>
            ) : null}
          </div>

          <div className="rounded-md border border-border/70 p-3 space-y-2">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium">
              Truck-level · freight
            </p>
            {(isFor || includeFreight) ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Transport company</p>
                    <p className="font-medium break-words">
                      {freight.transport_company_name || freight.transporter_name || "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Transporter</p>
                    <p className="font-medium break-words">
                      {freight.transporter_same_as_company
                        ? (freight.transport_company_name || freight.transporter_name || "—")
                        : (freight.transporter_name || freight.transporter_owner_name || "—")}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Rate</p>
                    <p className="font-medium">₹{freight.freight_rate_per_qtl || "—"}/qtl</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Gross freight</p>
                    <p className="font-medium">
                      {formatInr(preview?.freight_settlement?.gross_freight)}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground">TDS (1023/1024)</p>
                    <p className="font-medium">
                      {formatInr(preview?.freight_settlement?.tds_amount)}
                    </p>
                    {preview?.freight_settlement?.tds_reason ? (
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        {preview.freight_settlement.tds_reason}
                      </p>
                    ) : null}
                    {freight.pan_no || freight.truck_owner_pan ? (
                      <p className="text-[10px] text-muted-foreground">
                        PAN {freight.pan_no || freight.truck_owner_pan}
                      </p>
                    ) : preview?.freight_settlement?.tds_pan_required ? (
                      <p className="text-[10px] text-amber-800">PAN not entered</p>
                    ) : null}
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Net payable</p>
                    <p className="font-medium text-primary">
                      {formatInr(preview?.freight_settlement?.net_freight_payable)}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
                  <span>Bilty {freight.bilty_number || "—"}</span>
                  <span>Advance {formatInr(Number(freight.advance_at_dispatch) || 0)}</span>
                  {(preview?.freight_settlement?.shortage_deduction || 0) > 0 ? (
                    <span>
                      Shortage {formatInr(preview.freight_settlement.shortage_deduction)}
                      {" "}({preview.freight_settlement.net_shortage_qtl} qtl)
                    </span>
                  ) : null}
                  {freight.halting_applicable ? (
                    <span>Halting {formatInr(Number(freight.halting_charges) || preview?.freight_settlement?.halting_charges || 0)}</span>
                  ) : null}
                </div>
              </>
            ) : (
              <p className="text-xs text-muted-foreground">Freight voucher waived for this truck-load.</p>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <Button type="button" size="sm" variant="outline" disabled={busy || step === 1} onClick={() => setStep((s) => Math.max(1, s - 1))}>
          Back
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={busy}
          onClick={goNext}
          className="bg-bansal-sage hover:bg-bansal-sageDark text-white"
        >
          {busy
            ? "Saving…"
            : step === 4
              ? (dispatch.accounts_refinalize_required ? "Save updated settlement" : "Confirm final settlement")
              : "Save & next"}
        </Button>
        <PostSettlementJournalButton dispatch={dispatch} onSynced={() => onSaved?.()} />
      </div>
    </div>
  );
}
